// src/components/dashboard/RecentSms.js
import React from 'react';
import styled from 'styled-components';
import { 
  FaSms, 
  FaLongArrowAltLeft, 
  FaLongArrowAltRight,
  FaUser,
  FaReply
} from 'react-icons/fa';
import DashboardCard from './DashboardCard';
import { Link } from 'react-router-dom';
import { useCommunication } from '../../modules/communications/context/CommunicationContext';

const SmsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

const SmsItem = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  padding: ${props => props.theme.spacing(2)};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(4px);
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const SmsIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => 
    props.direction === 'inbound' 
      ? props.theme.colors.success + '22'
      : props.theme.colors.primary + '22'
  };
  color: ${props => 
    props.direction === 'inbound' 
      ? props.theme.colors.success
      : props.theme.colors.primary
  };
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SmsContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SmsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  margin-bottom: ${props => props.theme.spacing(0.5)};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SmsName = styled(Link)`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textPrimary};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

const SmsTime = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
`;

const SmsDirection = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const SmsBody = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textPrimary};
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const ReplyButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  border-radius: ${props => props.theme.borderRadius};
  font-size: ${props => props.theme.typography.fontSize.small};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  align-self: flex-start;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary}22;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing(4)};
  color: ${props => props.theme.colors.textSecondary};
`;

// Helper to format phone numbers
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

// Helper to format date
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString();
};

const RecentSms = ({ data, loading, error }) => {
  const { openSmsComposer } = useCommunication();

  const handleReply = (sms, e) => {
    e.preventDefault();
    
    const phoneNumber = sms.direction === 'inbound' ? sms.from_number : sms.to_number;
    const leadId = sms.Lead?.id;
    const leadName = sms.Lead?.name;
    
    openSmsComposer(phoneNumber, leadId, leadName);
  };

  return (
    <DashboardCard
      title="Recent Messages"
      icon={<FaSms />}
      viewAllLink="/communications/all"
      loading={loading}
      error={error}
    >
      {data && data.length > 0 ? (
        <SmsWrapper>
          {data.map((sms, index) => (
            <SmsItem key={`sms-${sms.sms_id || index}`}>
              <SmsIcon direction={sms.direction}>
                <FaSms />
              </SmsIcon>
              
              <SmsContent>
                <SmsHeader>
                  {sms.Lead ? (
                    <SmsName to={`/leads/view/${sms.Lead.id}`}>
                      <FaUser />
                      {sms.Lead.name}
                    </SmsName>
                  ) : (
                    <SmsName to={`/communications/placeholder-leads/${sms.from_number}`}>
                      {formatPhoneNumber(sms.from_number)}
                    </SmsName>
                  )}
                  
                  <SmsTime>{formatDateTime(sms.sent_time)}</SmsTime>
                </SmsHeader>
                
                <SmsDirection>
                  {sms.direction === 'inbound' ? (
                    <>
                      <FaLongArrowAltLeft />
                      Incoming from {formatPhoneNumber(sms.from_number)}
                    </>
                  ) : (
                    <>
                      <FaLongArrowAltRight />
                      Outgoing to {formatPhoneNumber(sms.to_number)}
                    </>
                  )}
                </SmsDirection>
                
                <SmsBody>{sms.body}</SmsBody>
                
                <ReplyButton onClick={(e) => handleReply(sms, e)}>
                  <FaReply />
                  Reply
                </ReplyButton>
              </SmsContent>
            </SmsItem>
          ))}
        </SmsWrapper>
      ) : (
        <EmptyState>No recent messages found</EmptyState>
      )}
    </DashboardCard>
  );
};

export default RecentSms;